import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/** Prefer progressive MP4 for short looping UGC — avoids HLS ladder downloads. */
const MAX_PLAYING = 2;
const ROOT_MARGIN = '200px 0px';

/**
 * UGC gallery: viewport autoplay for muted looping cards.
 * Sources stay on `data-src` until near viewport so below-fold HLS/mp4 does not compete with ATF.
 * Honours `prefers-reduced-motion` (poster only). Hero / at-your-home use `video-background-component` with the same near-viewport source attach.
 *
 * @extends {Component}
 */
class DevUgcGallery extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {Set<HTMLVideoElement>} */
  #playing = new Set();

  /** Videos currently reported intersecting — used to abort play() that finished after scroll-away. */
  /** @type {WeakSet<HTMLVideoElement>} */
  #intersecting = new WeakSet();

  connectedCallback() {
    super.connectedCallback();
    this.#setupObserver();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;

    for (const video of this.#playing) {
      video.pause();
    }

    this.#playing.clear();
    super.disconnectedCallback();
  }

  #setupObserver() {
    if (this.#observer) return;

    const videos = this.querySelectorAll('[data-ugc-video]');
    if (!videos.length) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) continue;

          if (entry.isIntersecting) {
            this.#intersecting.add(video);
            this.#activate(video);
          } else {
            this.#intersecting.delete(video);
            this.#deactivate(video);
          }
        }
      },
      { root: null, rootMargin: ROOT_MARGIN, threshold: 0.15 }
    );

    for (const video of videos) {
      this.#observer.observe(video);
    }
  }

  /**
   * @param {HTMLVideoElement} video
   */
  async #activate(video) {
    if (prefersReducedMotion()) return;

    this.#enforceCap(video);
    this.#attachSources(video);

    video.hidden = false;
    video.muted = true;
    video.setAttribute('autoplay', '');
    video.autoplay = true;

    try {
      await video.play();

      // Scroll-away during buffer — do not mark playing/ready.
      if (!this.#intersecting.has(video)) {
        video.pause();
        return;
      }

      this.#playing.add(video);
      video.closest('[data-ugc-video-wrap]')?.setAttribute('data-video-ready', '');
    } catch {
      video.removeAttribute('autoplay');
      video.hidden = true;
    }
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #deactivate(video) {
    video.pause();
    this.#playing.delete(video);
    video.closest('[data-ugc-video-wrap]')?.removeAttribute('data-video-ready');
  }

  /**
   * @param {HTMLVideoElement} keep
   */
  #enforceCap(keep) {
    if (this.#playing.size < MAX_PLAYING) return;

    for (const video of [...this.#playing]) {
      if (video === keep) continue;
      this.#deactivate(video);
      if (this.#playing.size < MAX_PLAYING) break;
    }
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #attachSources(video) {
    if (video.dataset.ugcSourcesAttached === 'true') return;

    const sources = video.querySelectorAll('source[data-src]');
    if (!sources.length) return;

    for (const source of sources) {
      const src = source.getAttribute('data-src');
      if (!src) continue;
      source.setAttribute('src', src);
      source.removeAttribute('data-src');
    }

    video.dataset.ugcSourcesAttached = 'true';
    video.load();
  }
}

if (!customElements.get('dev-ugc-gallery')) {
  customElements.define('dev-ugc-gallery', DevUgcGallery);
}
